/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { authenticationGetFullAccess } from '../fn/authentication/authentication-get-full-access';
import { AuthenticationGetFullAccess$Params } from '../fn/authentication/authentication-get-full-access';
import { authenticationLogin } from '../fn/authentication/authentication-login';
import { AuthenticationLogin$Params } from '../fn/authentication/authentication-login';
import { authenticationLoginLight } from '../fn/authentication/authentication-login-light';
import { AuthenticationLoginLight$Params } from '../fn/authentication/authentication-login-light';
import { authenticationLoginWithCertificate } from '../fn/authentication/authentication-login-with-certificate';
import { AuthenticationLoginWithCertificate$Params } from '../fn/authentication/authentication-login-with-certificate';
import { authenticationSendMfaEmail } from '../fn/authentication/authentication-send-mfa-email';
import { AuthenticationSendMfaEmail$Params } from '../fn/authentication/authentication-send-mfa-email';
import { authenticationValidateMfaCode } from '../fn/authentication/authentication-validate-mfa-code';
import { AuthenticationValidateMfaCode$Params } from '../fn/authentication/authentication-validate-mfa-code';
import { TokenResponseModel } from '../models/token-response-model';

@Injectable({ providedIn: 'root' })
export class AuthenticationService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `authenticationLogin()` */
  static readonly AuthenticationLoginPath = '/v1/Authentication/Login';

  /**
   * <br />Anonymous access
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `authenticationLogin()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  authenticationLogin$Response(params?: AuthenticationLogin$Params, context?: HttpContext): Observable<StrictHttpResponse<TokenResponseModel>> {
    return authenticationLogin(this.http, this.rootUrl, params, context);
  }

  /**
   * <br />Anonymous access
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `authenticationLogin$Response()` instead.
   *
   * This method sends `application/*+json` and handles request body of type `application/*+json`.
   */
  authenticationLogin(params?: AuthenticationLogin$Params, context?: HttpContext): Observable<TokenResponseModel> {
    return this.authenticationLogin$Response(params, context).pipe(
      map((r: StrictHttpResponse<TokenResponseModel>): TokenResponseModel => r.body)
    );
  }

  /** Path part for operation `authenticationLoginLight()` */
  static readonly AuthenticationLoginLightPath = '/v1/Authentication/LoginLight';

  /**
   * Login light :
   * Checks if access type is Kiosk or home. If it's not the case, exception is thrown because access type is not valid 
   * Otherwise, user's JWT token (with 'anonymous' login) is generated.
   *
   * It will return the JWT token (with 'anonymous' login) generated for the user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `authenticationLoginLight()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationLoginLight$Response(params?: AuthenticationLoginLight$Params, context?: HttpContext): Observable<StrictHttpResponse<TokenResponseModel>> {
    return authenticationLoginLight(this.http, this.rootUrl, params, context);
  }

  /**
   * Login light :
   * Checks if access type is Kiosk or home. If it's not the case, exception is thrown because access type is not valid 
   * Otherwise, user's JWT token (with 'anonymous' login) is generated.
   *
   * It will return the JWT token (with 'anonymous' login) generated for the user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `authenticationLoginLight$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationLoginLight(params?: AuthenticationLoginLight$Params, context?: HttpContext): Observable<TokenResponseModel> {
    return this.authenticationLoginLight$Response(params, context).pipe(
      map((r: StrictHttpResponse<TokenResponseModel>): TokenResponseModel => r.body)
    );
  }

  /** Path part for operation `authenticationGetFullAccess()` */
  static readonly AuthenticationGetFullAccessPath = '/v1/Authentication/GetFullAccess';

  /**
   * <br />ACTION_CODE_POLICY: LIGHT_LOGIN_USER
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `authenticationGetFullAccess()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationGetFullAccess$Response(params?: AuthenticationGetFullAccess$Params, context?: HttpContext): Observable<StrictHttpResponse<TokenResponseModel>> {
    return authenticationGetFullAccess(this.http, this.rootUrl, params, context);
  }

  /**
   * <br />ACTION_CODE_POLICY: LIGHT_LOGIN_USER
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `authenticationGetFullAccess$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationGetFullAccess(params?: AuthenticationGetFullAccess$Params, context?: HttpContext): Observable<TokenResponseModel> {
    return this.authenticationGetFullAccess$Response(params, context).pipe(
      map((r: StrictHttpResponse<TokenResponseModel>): TokenResponseModel => r.body)
    );
  }

  /** Path part for operation `authenticationValidateMfaCode()` */
  static readonly AuthenticationValidateMfaCodePath = '/v1/Authentication/ValidateMfaCode';

  /**
   * Validate the MFA code generated during user authentication if they did not have an active session.
   *
   * <br />ACTION_CODE_POLICY: AuthenticatedUser
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `authenticationValidateMfaCode()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationValidateMfaCode$Response(params?: AuthenticationValidateMfaCode$Params, context?: HttpContext): Observable<StrictHttpResponse<TokenResponseModel>> {
    return authenticationValidateMfaCode(this.http, this.rootUrl, params, context);
  }

  /**
   * Validate the MFA code generated during user authentication if they did not have an active session.
   *
   * <br />ACTION_CODE_POLICY: AuthenticatedUser
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `authenticationValidateMfaCode$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationValidateMfaCode(params?: AuthenticationValidateMfaCode$Params, context?: HttpContext): Observable<TokenResponseModel> {
    return this.authenticationValidateMfaCode$Response(params, context).pipe(
      map((r: StrictHttpResponse<TokenResponseModel>): TokenResponseModel => r.body)
    );
  }

  /** Path part for operation `authenticationSendMfaEmail()` */
  static readonly AuthenticationSendMfaEmailPath = '/v1/Authentication/SendMFAEmail';

  /**
   * Resend mail with MFA Code or recreate new session if oldest has expired.
   *
   * <br />ACTION_CODE_POLICY: AuthenticatedUser
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `authenticationSendMfaEmail()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationSendMfaEmail$Response(params?: AuthenticationSendMfaEmail$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return authenticationSendMfaEmail(this.http, this.rootUrl, params, context);
  }

  /**
   * Resend mail with MFA Code or recreate new session if oldest has expired.
   *
   * <br />ACTION_CODE_POLICY: AuthenticatedUser
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `authenticationSendMfaEmail$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationSendMfaEmail(params?: AuthenticationSendMfaEmail$Params, context?: HttpContext): Observable<void> {
    return this.authenticationSendMfaEmail$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `authenticationLoginWithCertificate()` */
  static readonly AuthenticationLoginWithCertificatePath = '/v1/Authentication/LoginWithCertificate';

  /**
   * Login with certificate : 
   * This function gets information about user certificate
   * Checks if it matches with user's certificate in DB
   * Checks if user is active
   * Grants additional actions if access type is kiosk
   * Then user's JWT token is generated.
   *
   * It will return the JWT token generated for the user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `authenticationLoginWithCertificate()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationLoginWithCertificate$Response(params?: AuthenticationLoginWithCertificate$Params, context?: HttpContext): Observable<StrictHttpResponse<TokenResponseModel>> {
    return authenticationLoginWithCertificate(this.http, this.rootUrl, params, context);
  }

  /**
   * Login with certificate : 
   * This function gets information about user certificate
   * Checks if it matches with user's certificate in DB
   * Checks if user is active
   * Grants additional actions if access type is kiosk
   * Then user's JWT token is generated.
   *
   * It will return the JWT token generated for the user
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `authenticationLoginWithCertificate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  authenticationLoginWithCertificate(params?: AuthenticationLoginWithCertificate$Params, context?: HttpContext): Observable<TokenResponseModel> {
    return this.authenticationLoginWithCertificate$Response(params, context).pipe(
      map((r: StrictHttpResponse<TokenResponseModel>): TokenResponseModel => r.body)
    );
  }

}
